import {
    Injectable, inject
} from "@angular/core";
import {
    ChainUtil,
    FileRequestSubject,
    FileResponse,
    RequestUtil
} from "@shopliftr/common-js/shared";
import { ChainImageUploadOptions } from "@shopliftr/common-js/admin";
import {
    Language, MimeType
} from "@shopliftr/common-js/data";
import {
    Observable, throwError
} from "rxjs";

import { IInternalApiFile } from "../../../../interfaces/internal-api-file";
import { InternalApiFileRequestService } from "../../request/internal-api/internal-api-file-request.service";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";

/**
 * API File Service for ChainImage file operations.
 *
 * Handles image uploads for chains, delegating HTTP communication
 * to InternalApiFileRequestService.
 *
 * @pattern API File Service / ChainImage File Management
 * @providedIn root
 * @implements {IInternalApiFile}
 */
@Injectable({ providedIn: "root" })
export class ChainImageApiFileService implements IInternalApiFile {

    readonly fileRequestSubject = FileRequestSubject.ChainImage;

    private readonly _internalApiFileRequestService = inject(InternalApiFileRequestService);

    /**
     * Uploads an image for a chain.
     *
     * @param chainId - Chain identifier
     * @param imageFile - File blob to upload
     * @param language - Optional language for localized images
     * @returns Observable that emits the file upload response
     */
    upload$(chainId: string, imageFile: Blob, language?: Language): Observable<FileResponse> {

        if (chainId) {
            const options = new ChainImageUploadOptions();
            options.setDefaults();
            options.id = chainId;
            if (language) {
                options.language = language;
            }
            const filePath = ChainUtil.getImageFilePath(chainId, imageFile.type as MimeType, language);
            const request = RequestUtil.createFileUploadRequest(
                FileRequestSubject.ChainImage, filePath, imageFile.type as MimeType, options
            );
            return this._internalApiFileRequestService.uploadFile$(request, imageFile);
        }
        return throwError(() => new InvalidParametersException("No chainId was provided, chain image cannot be uploaded"));
    }
}
